import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const PINNED_ITEMS_KEY = 'pinned_sidebar_items';

const toPinnableItem = (item, group, subGroup) => ({
  key: item.pinKey ?? item.name,
  label: item.label,
  groupLabel: group?.label,
  icon: item.icon ?? subGroup?.icon ?? group?.icon,
  to: item.to,
  activeOn: item.activeOn,
  badgeCount: item.badgeCount,
  getterKeys: item.getterKeys,
});

// Achata o menu em itens clicáveis: cabeçalhos sem rota (grupos e subgrupos) ficam de fora.
// O grupo só entra no rótulo ("Grupo › Item") quando outro item do menu tem o mesmo nome;
// a checagem usa o menu inteiro, não só os fixados, para o rótulo não mudar ao fixar outro item.
export const flattenPinnableItems = menuItems => {
  const items = menuItems
    .flatMap(group => {
      if (!group.children) return [toPinnableItem(group)];
      return group.children.flatMap(child =>
        child.children
          ? child.children.map(leaf => toPinnableItem(leaf, group, child))
          : [toPinnableItem(child, group)]
      );
    })
    .filter(item => item.to);

  return items.map(item => {
    const isAmbiguous =
      items.filter(other => other.label === item.label).length > 1;
    return {
      ...item,
      fullLabel:
        isAmbiguous && item.groupLabel
          ? `${item.groupLabel} › ${item.label}`
          : item.label,
    };
  });
};

// Chaves sem item correspondente (excluído, sem permissão, oculto) são ignoradas.
export const pickPinnedItems = (keys, items) =>
  keys.map(key => items.find(item => item.key === key)).filter(Boolean);

export function usePinnedSidebarItems() {
  const { t } = useI18n();
  const { uiSettings, updateUISettings } = useUISettings();
  const accountId = useMapGetter('getCurrentAccountId');

  const pinnedKeys = computed(
    () => uiSettings.value[PINNED_ITEMS_KEY]?.[accountId.value] ?? []
  );

  // O backend substitui o ui_settings inteiro e o merge do composable é só de primeiro
  // nível: as listas das outras contas precisam ser reenviadas junto.
  // A mudança já vale na tela antes de salvar; se o salvamento falhar, ela só dura até recarregar.
  const savePinnedKeys = async keys => {
    const saved = await updateUISettings({
      [PINNED_ITEMS_KEY]: {
        ...uiSettings.value[PINNED_ITEMS_KEY],
        [accountId.value]: keys,
      },
    });
    if (!saved) useAlert(t('SIDEBAR.PINNED_ITEMS_SAVE_ERROR'));
  };

  const isPinned = key => pinnedKeys.value.includes(key);

  const togglePin = key =>
    savePinnedKeys(
      isPinned(key)
        ? pinnedKeys.value.filter(pinnedKey => pinnedKey !== key)
        : [...pinnedKeys.value, key]
    );

  // Recebe só as chaves visíveis; as ocultas vão para o fim para voltarem se o acesso voltar.
  const reorder = orderedKeys =>
    savePinnedKeys([
      ...orderedKeys,
      ...pinnedKeys.value.filter(key => !orderedKeys.includes(key)),
    ]);

  return { pinnedKeys, isPinned, togglePin, reorder };
}
